import styled from '@emotion/styled'
import { Box, Typography } from '@mui/material'

import Modal from '@f/components/Modal/Modal'

import MonthlyRateCalculatorModal from '@f-fertighaus/applications/MonthlyRateCalculatorModal/MonthlyRateCalculatorModal'
import { InfoTooltip } from '@f-fertighaus/applications/Next/components/fertighaus/InfoTooltip/InfoTooltip'
import { Domain } from '@f-fertighaus/applications/Next/pages/house-detail-page/HDPContent'
import {
  DictionaryKey,
  t
} from '@f-fertighaus/applications/Next/public/locales/de'
import { EXPANSION_STAGE_BY_DISPLAY_NAME } from '@f-fertighaus/applications/Next/utils/backendStructureMappings'
import { useHouseDetail } from '@f-fertighaus/applications/Next/utils/context/HouseDetail'
import { PRICE } from '@f-fertighaus/applications/Next/utils/price'
import { webtrack } from '@f-fertighaus/applications/Next/utils/webtrack'

function openMonthlyRateCalculator({
  price,
  expansionStage,
  domain
}: {
  price: number
  expansionStage: string
  domain: Domain
}) {
  const modal = new Modal()
  modal.openModal({
    component: (
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      <MonthlyRateCalculatorModal
        {...window.SherlockContainer.BudgetCalculator}
        housePrice={price}
        expansionStage={expansionStage}
        isModal
        domain={domain}
      />
    )
  })
}

export const PriceSection = () => {
  const { state, domain } = useHouseDetail()

  const mainExpansionStageType =
    EXPANSION_STAGE_BY_DISPLAY_NAME[state.expansion_stage_display]

  const handleMonthlyRateButtonClick = () => {
    if (state.price_display) {
      openMonthlyRateCalculator({
        price: state.price_display,
        expansionStage: state.expansion_stage_display,
        domain
      })
    }
    webtrack({
      category: 'CTA-button',
      action: 'click',
      label: 'monthly-rate',
      location: 'sticky-summary',
      values: [
        {
          type: 'text',
          value: t('houseDetailPage.sections.price.monthlyRateFrom', {
            price: PRICE.formatPrice(
              PRICE.calculateMonthlyRate(state.price_display as number)
            )
          })
        }
      ]
    })
  }

  return (
    <PriceSectionContainer>
      <Box sx={{ display: 'flex' }}>
        <Typography variant='mediumTitle' component='p' data-sel='house-price'>
          {state.price_display
            ? t('houseDetailPage.houseOverview.price', {
                price: PRICE.formatPrice(state.price_display)
              })
            : t('houseDetailPage.houseOverview.price-missing')}
        </Typography>
        <TooltipWrapper>
          <InfoTooltip
            title={t('houseDetailPage.sections.price.tooltip')}
            wtPayload={{ label: 'price', location: 'sticky-summary' }}
          />
        </TooltipWrapper>
      </Box>
      <Typography component='p' variant='body2'>
        {t(
          `houseDetailPage.sections.details.overview.values.expansion_stage.${mainExpansionStageType}` as DictionaryKey
        )}
      </Typography>
      {state.price_display ? (
        <MonthlyRate onClick={handleMonthlyRateButtonClick}>
          {t('houseDetailPage.sections.price.monthlyRateFrom', {
            price: PRICE.formatPrice(
              PRICE.calculateMonthlyRate(state.price_display)
            )
          })}
        </MonthlyRate>
      ) : (
        <Typography variant='body2' component='p'>
          {t('houseDetailPage.houseOverview.price-missing')}
        </Typography>
      )}
    </PriceSectionContainer>
  )
}

const PriceSectionContainer = styled(Box)`
  grid-area: sidebar-price;
  label: SidebarPriceContainer;
`
const TooltipWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-left: ${({ theme }) => theme.spacing(1)};
  label: TooltipWrapper;
`

const MonthlyRate = styled('button')`
  color: ${({ theme }) => theme.palette.text.primary};
  font-family: ${({ theme }) => theme.font.base};
  font-size: 14px;
  background: none;
  border: none;
  padding: 0;
  &:hover {
    cursor: pointer;
  }
}
`
